export const SHORT_STEP_MIN_WIDTH_PX = 5;
export const ABANDONED_COLOR = '#667780';

export type StepCompletionStatus = 'completed' | 'failed' | 'skipped' | 'aborted' | 'started' | 'unknown';

type ExecutionEventLike = {
    name?: string | null;
    description?: string | null;
    type?: string | null;
    timestamp?: string | null;
    parentEventId?: string | null;
    value?: unknown;
};

type ExecutionStepLike = {
    id?: string | null;
    name?: string | null;
    EventId?: string | null;
    eventId?: string | null;
    startTime?: string | null;
    endTime?: string | null;
    details?: Record<string, unknown> | null;
    status?: string | null;
    completionStatus?: string | null;
    stepCompletionStatus?: string | null;
    result?: string | null;
    success?: boolean | null;
};

export type StepMediaItem = {
    id: string;
    url: string;
    label: string;
    mimeType: string | null;
    timeStamp: string | null;
    stepId: string | null;
    kind: 'audio' | 'video' | 'unknown';
};

export const ABANDONED_IDLE_MINUTES = 10;
export const ABANDONED_STARTED_MINUTES = 30;

export type ExecutionLifecycleState = 'completed' | 'failed' | 'aborted' | 'ongoing' | 'abandoned';

export type ExecutionStatusMeta = {
    state: ExecutionLifecycleState;
    label: string;
    title: string;
    description: string;
    color: string;
    background: string;
    labelColor?: string;
};

type ExecutionLike = {
    endTime?: string | null;
    endReason?: string | null;
    isCompleted?: boolean | null;
    success?: boolean | null;
    startTime?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    steps?: ExecutionStepLike[] | null;
    events?: ExecutionEventLike[] | null;
    mediaEvents?: Array<Record<string, unknown>> | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function getDetailsRecord(details: unknown): Record<string, unknown> {
    return isRecord(details) ? details : {};
}

function getValueRecord(value: unknown): Record<string, unknown> {
    return isRecord(value) ? value : {};
}

function getCandidateStrings(record: Record<string, unknown>, keys: string[]): string[] {
    return keys
        .map(key => getStringValue(record[key]))
        .filter((value): value is string => Boolean(value));
}

function getStringValue(value: unknown): string | null {
    if (typeof value === 'string') {
        const trimmed = value.trim();

        return trimmed.length > 0 ? trimmed : null;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }

    return null;
}

function getBooleanValue(value: unknown): boolean {
    if (typeof value === 'boolean') {return value;}
    if (typeof value === 'string') {
        return ['true', '1', 'yes', 'mandatory'].includes(value.trim().toLowerCase());
    }
    if (typeof value === 'number') {return value === 1;}

    return false;
}

function normalizeStatus(value: unknown): StepCompletionStatus {
    const raw = getStringValue(value)?.toLowerCase();

    if (!raw) {return 'unknown';}

    if (raw.includes('skip')) {return 'skipped';}
    if (raw.includes('abort') || raw.includes('cancel')) {return 'aborted';}
    if (raw.includes('fail') || raw.includes('error')) {return 'failed';}
    if (raw.includes('complete') || raw.includes('done') || raw.includes('success')) {return 'completed';}

    switch (raw) {
                    case 'completed':
                    case 'complete':
                    case 'done':
                    case 'success':
                    case 'succeeded':
                        return 'completed';
                    case 'failed':
                    case 'fail':
                    case 'error':
                        return 'failed';
                    case 'skipped':
                    case 'skip':
                        return 'skipped';
                    case 'aborted':
                    case 'abort':
                    case 'cancelled':
                    case 'canceled':
                        return 'aborted';
                    default:
                        return 'unknown';
    }
}

export function getStepDesignedNumber(step: Pick<ExecutionStepLike, 'details'>): string | null {
    const details = getDetailsRecord(step.details);

    return (
        getStringValue(details.StepNumber) ??
        getStringValue(details.DesignedStepNumber) ??
        getStringValue(details.StepNo) ??
        null
    );
}

export function isMandatoryStep(step: Pick<ExecutionStepLike, 'details'>): boolean {
    const details = getDetailsRecord(step.details);

    return getBooleanValue(details.IsMandatory ?? details.isMandatory ?? details.Mandatory);
}

export function getStepEventId(step: ExecutionStepLike): string | null {
    const details = getDetailsRecord(step.details);

    return (
        getStringValue(step.EventId) ??
        getStringValue(step.eventId) ??
        getStringValue(details.EventId) ??
        getStringValue(details.eventId) ??
        null
    );
}

function getStepMatchCandidates(step: ExecutionStepLike): string[] {
    const details = getDetailsRecord(step.details);

    return Array.from(new Set([
        getStringValue(step.id),
        getStepEventId(step),
        getStepDesignedNumber(step),
        getStringValue(details.StepId),
        getStringValue(details.stepId),
    ].filter((value): value is string => Boolean(value))));
}

function getEventParentStepId(event: ExecutionEventLike): string | null {
    const eventValue = getValueRecord(event.value);

    return (
        getStringValue(event.parentEventId) ??
        getStringValue(eventValue.parentEventId) ??
        getStringValue(eventValue.ParentEventId) ??
        null
    );
}

export function getExecutionLatestActivityMs(execution: ExecutionLike): number {
    const eventTimes = (execution.events ?? [])
        .map(event => Date.parse(event.timestamp ?? ''))
        .filter(value => Number.isFinite(value));

    const mediaEventTimes = (execution.mediaEvents ?? [])
        .map(event => Date.parse(getStringValue(event.timestamp ?? event.timeStamp ?? event.createdAt) ?? ''))
        .filter(value => Number.isFinite(value));

    const stepTimes = (execution.steps ?? [])
        .flatMap(step => [Date.parse(step.startTime ?? ''), Date.parse(step.endTime ?? '')])
        .filter(value => Number.isFinite(value));

    return Math.max(
        ...eventTimes,
        ...mediaEventTimes,
        ...stepTimes,
        Date.parse(execution.updatedAt ?? ''),
        Date.parse(execution.createdAt ?? ''),
        Date.parse(execution.startTime ?? ''),
        0,
    );
}

export function getExecutionLifecycleState(execution: ExecutionLike, abandonedIdleMinutes = ABANDONED_IDLE_MINUTES): ExecutionLifecycleState {
    if (execution.endTime) {
        const normalizedEndReason = String(execution.endReason ?? '').toLowerCase();

        if (normalizedEndReason.includes('abort') || normalizedEndReason.includes('cancel')) {
            return 'aborted';
        }

        if (execution.success === false || execution.isCompleted === false) {
            return 'failed';
        }

        return 'completed';
    }

    const startedAtMs = Date.parse(execution.startTime ?? execution.createdAt ?? '');
    const startedThresholdMs = ABANDONED_STARTED_MINUTES * 60 * 1000;

    if (Number.isFinite(startedAtMs) && Date.now() - startedAtMs >= startedThresholdMs) {
        return 'abandoned';
    }

    const latestActivityMs = getExecutionLatestActivityMs(execution);
    const thresholdMs = abandonedIdleMinutes * 60 * 1000;

    return latestActivityMs > 0 && Date.now() - latestActivityMs >= thresholdMs ? 'abandoned' : 'ongoing';
}

function toTitleCaseWords(value: string): string {
    return value
        .toLowerCase()
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map(token => token.charAt(0).toUpperCase() + token.slice(1))
        .join(' ');
}

function getExecutionOutcomeLabel(execution: ExecutionLike, lifecycleState: ExecutionLifecycleState): string {
    const normalizedEndReason = String(execution.endReason ?? '').trim().toLowerCase();

    if (lifecycleState === 'failed') {
        if (normalizedEndReason.includes('exception') || execution.isCompleted === false) {
            return 'Exception';
        }

        return 'Failed';
    }

    if (lifecycleState === 'aborted') {
        return 'Stopped';
    }

    if (lifecycleState === 'abandoned') {
        return 'Abandoned';
    }

    if (lifecycleState === 'ongoing') {
        return 'In Progress';
    }

    if (!normalizedEndReason || normalizedEndReason === 'completed') {
        return 'Completed';
    }

    return toTitleCaseWords(normalizedEndReason);
}

export function getExecutionStatusMeta(execution: ExecutionLike, theme: {
    colors: {
        green: string;
        red: string;
        strong_yellow: string;
    };
    isDark?: boolean;
}): ExecutionStatusMeta {
    const state = getExecutionLifecycleState(execution);
    const label = getExecutionOutcomeLabel(execution, state);

    switch (state) {
                    case 'completed':
                        return {
                            state,
                            label,
                            title: label,
                            description: 'The session completed successfully',
                            color: theme.colors.green,
                            background: 'rgba(16, 185, 129, 0.14)',
                        };
                    case 'failed':
                        return {
                            state,
                            label,
                            title: label,
                            description: label === 'Exception'
                                ? 'The session stopped because of an exception before completion'
                                : 'The session finished, but the outcome was unsuccessful',
                            color: theme.colors.red,
                            background: 'rgba(220, 38, 38, 0.12)',
                        };
                    case 'aborted':
                        return {
                            state,
                            label,
                            title: label,
                            description: 'The session was stopped before completion',
                            color: theme.isDark ? '#e8a060' : '#523a3a',
                            background: 'rgba(245, 158, 11, 0.14)',
                        };
                    case 'abandoned':
                        return {
                            state,
                            label,
                            title: label,
                            description: 'The session did not finish within 30 minutes or has been idle for at least 10 minutes',
                            color: ABANDONED_COLOR,
                            background: 'rgba(102, 119, 128, 0.14)',
                        };
                    case 'ongoing':
                    default:
                        return {
                            state,
                            label,
                            title: label,
                            description: 'The session is still ongoing',
                            color: theme.colors.strong_yellow,
                            labelColor: '#d18100',
                            background: 'rgba(234, 179, 8, 0.16)',
                        };
    }
}

export function isErrorEventType(value: string | null | undefined): boolean {
    const normalized = getStringValue(value)?.toLowerCase();

    return Boolean(normalized && normalized.includes('error'));
}

export function getStepCompletionStatus(step: ExecutionStepLike): StepCompletionStatus {
    const details = getDetailsRecord(step.details);

    if (getBooleanValue(details.IsSkipped ?? details.isSkipped ?? details.Skipped)) {return 'skipped';}
    if (getBooleanValue(details.IsAborted ?? details.isAborted ?? details.Aborted)) {return 'aborted';}
    if (getBooleanValue(details.IsFailed ?? details.isFailed ?? details.Failed)) {return 'failed';}
    if (getBooleanValue(details.IsCompleted ?? details.isCompleted ?? details.Completed)) {return 'completed';}

    const candidates: unknown[] = [
        step.completionStatus,
        step.stepCompletionStatus,
        step.status,
        step.result,
        details.EndReason,
        details.Reason,
        details.CompletionStatus,
        details.StepCompletionStatus,
        details.Status,
        details.Result,
    ];

    for (const candidate of candidates) {
        const status = normalizeStatus(candidate);
        if (status !== 'unknown') {
            return status;
        }
    }

    if (step.success === true) {return 'completed';}
    if (step.success === false) {return 'failed';}

    if (getStringValue(step.startTime) && !getStringValue(step.endTime)) {return 'started';}

    return 'completed';
}

function getStepWindow(execution: ExecutionLike, step: ExecutionStepLike): { startMs: number; endMs: number } | null {
    const startMs = Date.parse(step.startTime ?? '');
    if (!Number.isFinite(startMs)) {return null;}

    const explicitEndMs = Date.parse(step.endTime ?? '');
    if (Number.isFinite(explicitEndMs)) {
        return { startMs, endMs: explicitEndMs };
    }

    const nextStartMs = (execution.steps ?? [])
        .filter(candidate => candidate.id !== step.id)
        .map(candidate => Date.parse(candidate.startTime ?? ''))
        .filter(candidateStartMs => Number.isFinite(candidateStartMs) && candidateStartMs > startMs)
        .sort((left, right) => left - right)[0];

    if (Number.isFinite(nextStartMs)) {
        return { startMs, endMs: nextStartMs as number };
    }

    const executionEndMs = Date.parse(execution.endTime ?? '');
    if (Number.isFinite(executionEndMs)) {
        return { startMs, endMs: executionEndMs };
    }

    return { startMs, endMs: startMs };
}

export function getStepEvents(execution: ExecutionLike, step: ExecutionStepLike): ExecutionEventLike[] {
    const window = getStepWindow(execution, step);
    if (!window) {return [];}

    const stepCandidates = getStepMatchCandidates(step);
    const executionStepIds = new Set((execution.steps ?? []).map(candidate => getStringValue(candidate.id)).filter((value): value is string => Boolean(value)));

    return (execution.events ?? []).filter(event => {
        const parentStepId = getEventParentStepId(event);
        if (parentStepId && executionStepIds.has(parentStepId)) {
            return step.id === parentStepId;
        }

        const eventValue = getValueRecord(event.value);
        const linkedCandidates = Array.from(new Set([
            ...getCandidateStrings(eventValue, ['_StepId', 'StepId', '_Step', 'Step', 'stepId']),
            ...getCandidateStrings(getValueRecord(eventValue._Step), ['Id', 'EventId', 'StepNumber', 'StepId', 'Name']),
        ]));

        if (linkedCandidates.length > 0 && stepCandidates.length > 0) {
            return linkedCandidates.some(candidate => stepCandidates.includes(candidate));
        }

        const timestampMs = Date.parse(event.timestamp ?? '');

        return Number.isFinite(timestampMs) && timestampMs >= window.startMs && timestampMs <= window.endMs;
    });
}

export function getStepErrorCount(execution: ExecutionLike, step: ExecutionStepLike): number {
    return getStepEvents(execution, step).filter(event => isErrorEventType(event.type)).length;
}

export function formatEventTypeLabel(value: string | null | undefined): string {
    const normalized = (value ?? '').trim();
    if (!normalized) {return 'Event';}

    return normalized
        .toLowerCase()
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map(token => token.charAt(0).toUpperCase() + token.slice(1))
        .join(' ');
}

export function isShortStep(durationSeconds: number, pixelsPerSecond: number): boolean {
    return durationSeconds * pixelsPerSecond < SHORT_STEP_MIN_WIDTH_PX;
}

export function hasDisplayableEventValue(value: unknown): boolean {
    if (value == null) {return false;}
    if (typeof value === 'string') {return value.trim().length > 0;}
    if (typeof value === 'number' || typeof value === 'boolean') {return true;}
    if (Array.isArray(value)) {return value.length > 0;}
    if (isRecord(value)) {
        return Object.entries(value).some(([key, raw]) => !key.startsWith('_') && raw != null && `${raw}`.trim() !== '');
    }

    return false;
}

export function getStepMediaItems(execution: ExecutionLike, step: ExecutionStepLike): StepMediaItem[] {
    const stepEvents = getStepEvents(execution, step);
    const normalizedStepId = getStringValue(step.id);
    const mediaEvents = (execution.mediaEvents ?? [])
        .filter(event => {
            const linkedStepId = getStringValue(event.stepId ?? event.StepId ?? event._StepId);

            return Boolean(linkedStepId && normalizedStepId && linkedStepId === normalizedStepId);
        })
        .map(event => ({
            name: getStringValue(event.name),
            description: getStringValue(event.description),
            type: getStringValue(event.type),
            timestamp: getStringValue(event.timestamp ?? event.timeStamp ?? event.createdAt),
            value: event,
        }));

    return [...stepEvents, ...mediaEvents]
        .map((event, index) => {
            const value = getValueRecord(event.value);
            const nestedMedia = getValueRecord(value.Media ?? value.media ?? value.File ?? value.file);
            const merged = { ...value, ...nestedMedia };

            const url = getCandidateStrings(merged, ['AudioUrl', 'audioUrl', 'MediaUrl', 'mediaUrl', 'Url', 'url', 'Uri', 'uri', 'FileUrl', 'fileUrl'])[0] ?? null;
            if (!url) {return null;}

            const mimeType = getCandidateStrings(merged, ['MimeType', 'mimeType', 'ContentType', 'contentType'])[0] ?? null;
            const typeHint = `${event.type ?? ''} ${mimeType ?? ''} ${url}`.toLowerCase();
            const kind: StepMediaItem['kind'] = typeHint.includes('audio') ? 'audio' : typeHint.includes('video') ? 'video' : 'unknown';
            const linkedStepId = getCandidateStrings(merged, ['stepId', 'StepId', '_StepId'])[0] ?? normalizedStepId ?? null;

            return {
                id: `${event.timestamp ?? 'event'}-${index}`,
                url,
                label: event.name ?? event.description ?? formatEventTypeLabel(event.type),
                mimeType,
                timeStamp: event.timestamp ?? null,
                stepId: linkedStepId,
                kind,
            } as StepMediaItem;
        })
        .filter((item): item is StepMediaItem => Boolean(item));
}